import { useEffect, type RefObject } from "react";

/**
 * List of selectors that match interactive elements considered focusable for
 * keyboard navigation inside a dialog. Anchors must have an `href`; form
 * controls and `[tabindex]` nodes must not be `[tabindex="-1"]` or
 * `[disabled]`. Kept loose on purpose — `disabled` form controls already get
 * excluded by their selectors, and `display:none` / `visibility:hidden`
 * elements are filtered out via `offsetParent` at runtime.
 */
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  "button:not([disabled])",
  "iframe",
  "object",
  "embed",
  "audio[controls]",
  "video[controls]",
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function isFocusable(el: HTMLElement): boolean {
  // `offsetParent === null` excludes `display:none` chains; an element with
  // `position: fixed` has a null offsetParent too, so accept those by
  // checking the bounding box.
  if (el.hasAttribute("disabled")) return false;
  if (el.getAttribute("aria-hidden") === "true") return false;
  if (el.offsetParent === null) {
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return false;
  }
  return true;
}

function getFocusable(container: HTMLElement): HTMLElement[] {
  const nodes = Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  );
  return nodes.filter(isFocusable);
}

/**
 * Trap keyboard focus inside `containerRef`, set initial focus, and restore
 * focus to the element that owned it before mount when the trap unmounts.
 *
 * Use on any `role="dialog"` / modal surface to satisfy WCAG 2.4.3 — keyboard
 * users must not be able to Tab into the inert page behind an open dialog.
 *
 * - Tab from the last focusable wraps to the first.
 * - Shift+Tab from the first focusable wraps to the last.
 * - On open, focus moves to the first focusable inside the container (or the
 *   container itself when it has `tabindex`, so it can still receive focus).
 * - On close, focus is returned to whatever owned it before the trap mounted,
 *   unless that element is gone from the DOM.
 *
 * The trap is a no-op until `active` becomes true so callers can render the
 * dialog while delaying focus capture (e.g. for animation entry).
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean = true,
): void {
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Defer initial focus to the next microtask so portals + framer-motion
    // entry transitions don't fight the focus call.
    queueMicrotask(() => {
      const focusables = getFocusable(container);
      const first = focusables[0];
      if (first) {
        first.focus({ preventScroll: true });
      } else {
        // Fall back to focusing the container itself so subsequent Tab
        // presses stay inside the dialog (the keydown handler relies on
        // document.activeElement being inside `container`).
        if (!container.hasAttribute("tabindex")) container.setAttribute("tabindex", "-1");
        container.focus({ preventScroll: true });
      }
    });

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const focusables = getFocusable(container);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const activeEl = document.activeElement as HTMLElement | null;

      // If focus has escaped the container (e.g. browser address bar
      // returned focus to body), pull it back to the first focusable.
      if (!activeEl || !container.contains(activeEl)) {
        e.preventDefault();
        first.focus({ preventScroll: true });
        return;
      }

      if (e.shiftKey && activeEl === first) {
        e.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      // Only restore focus if the previously focused element is still
      // attached. Otherwise leave focus where the closing animation put it
      // so screen readers don't read a stale region.
      if (previouslyFocused && document.contains(previouslyFocused)) {
        try {
          previouslyFocused.focus({ preventScroll: true });
        } catch {
          // Some elements (detached SVG, etc.) throw on `.focus()`.
        }
      }
    };
  }, [containerRef, active]);
}
